import { useState, useMemo } from 'react';
import type { OwnAd } from '@/mocks/ownAds';

const RADIO_OPTIONS = [
  { name: 'FM Horizonte', frequency: '98.7 FM', city: 'Mendoza' },
  { name: 'Radio Nexo', frequency: '104.3 FM', city: 'San Rafael' },
  { name: 'La Popular', frequency: '89.1 FM', city: 'Godoy Cruz' },
  { name: 'FM Del Sol', frequency: '96.5 FM', city: 'Las Heras' },
  { name: 'Radio Universidad', frequency: '92.9 FM', city: 'Mendoza' },
  { name: 'FM Estación', frequency: '101.7 FM', city: 'Maipú' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (ad: OwnAd) => void;
}

export default function OwnAdFormModal({ open, onClose, onCreate }: Props) {
  const [radioIndex, setRadioIndex] = useState(0);
  const [client, setClient] = useState('');
  const [plan, setPlan] = useState<'Básico' | 'Estándar' | 'Premium'>('Estándar');
  const [price, setPrice] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>();
  const [submitting, setSubmitting] = useState(false);

  const selectedRadio = RADIO_OPTIONS[radioIndex];

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!client.trim()) e.client = 'El cliente es obligatorio.';
    if (!price.trim() || Number(price) <= 0) e.price = 'El precio debe ser mayor a 0.';
    if (!startDate) e.startDate = 'La fecha de inicio es obligatoria.';
    if (!endDate) e.endDate = 'La fecha de fin es obligatoria.';
    if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
      e.endDate = 'La fecha de fin debe ser posterior a la de inicio.';
    }
    if (!content.trim()) e.content = 'El contenido es obligatorio.';
    if (content.trim().length > 500) e.content = 'Máximo 500 caracteres.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitting(true);
    const id = Date.now();
    const months = Math.max(1, Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24 * 30)));
    const newAd: OwnAd = {
      id,
      radioName: selectedRadio.name,
      radioFrequency: selectedRadio.frequency,
      radioCity: selectedRadio.city,
      plan,
      client: client.trim(),
      price: Number(price),
      startDate,
      endDate,
      content: content.trim(),
      status: 'pendiente',
      impressions: 0,
      clicks: 0,
      ctr: 0,
      revenue: Number(price) * months,
    };
    setTimeout(() => {
      onCreate(newAd);
      setSubmitting(false);
      reset();
      onClose();
    }, 600);
  };

  const reset = () => {
    setRadioIndex(0);
    setClient('');
    setPlan('Estándar');
    setPrice('');
    setStartDate('');
    setEndDate('');
    setContent('');
    setErrors({});
  };

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-lg">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-start justify-between gap-4 z-10">
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">Crear Campaña de Publicidad Propia</h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">La campaña quedará en estado Pendiente hasta ser aprobada.</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Radio */}
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Radio *</label>
            <select
              value={radioIndex}
              onChange={(e) => setRadioIndex(Number(e.target.value))}
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
            >
              {RADIO_OPTIONS.map((r, i) => (
                <option key={i} value={i}>
                  {r.name} · {r.frequency} · {r.city}
                </option>
              ))}
            </select>
          </div>

          {/* Cliente */}
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Cliente *</label>
            <input
              type="text"
              value={client}
              onChange={(e) => { setClient(e.target.value); setErrors(p => { const n = { ...p }; delete n.client; return n; }); }}
              placeholder="Nombre del cliente o marca"
              className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.client ? 'border-red-400' : 'border-background-200'}`}
            />
            {errors.client && <p className="text-red-500 text-[9px] mt-1">{errors.client}</p>}
          </div>

          {/* Plan + Precio */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Plan *</label>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value as 'Básico' | 'Estándar' | 'Premium')}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
              >
                <option value="Básico">Básico</option>
                <option value="Estándar">Estándar</option>
                <option value="Premium">Premium</option>
              </select>
            </div>
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Precio/mes (USD) *</label>
              <input
                type="number"
                min={1}
                value={price}
                onChange={(e) => { setPrice(e.target.value); setErrors(p => { const n = { ...p }; delete n.price; return n; }); }}
                placeholder="500"
                className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.price ? 'border-red-400' : 'border-background-200'}`}
              />
              {errors.price && <p className="text-red-500 text-[9px] mt-1">{errors.price}</p>}
            </div>
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Fecha inicio *</label>
              <input
                type="date"
                value={startDate}
                min={today}
                onChange={(e) => { setStartDate(e.target.value); setErrors(p => { const n = { ...p }; delete n.startDate; delete n.endDate; return n; }); }}
                className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors ${errors.startDate ? 'border-red-400' : 'border-background-200'}`}
              />
              {errors.startDate && <p className="text-red-500 text-[9px] mt-1">{errors.startDate}</p>}
            </div>
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Fecha fin *</label>
              <input
                type="date"
                value={endDate}
                min={startDate || today}
                onChange={(e) => { setEndDate(e.target.value); setErrors(p => { const n = { ...p }; delete n.endDate; return n; }); }}
                className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors ${errors.endDate ? 'border-red-400' : 'border-background-200'}`}
              />
              {errors.endDate && <p className="text-red-500 text-[9px] mt-1">{errors.endDate}</p>}
            </div>
          </div>

          {/* Contenido */}
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Contenido / Creatividad *</label>
            <textarea
              value={content}
              onChange={(e) => { setContent(e.target.value); setErrors(p => { const n = { ...p }; delete n.content; return n; }); }}
              rows={4}
              placeholder="Describe la campaña, ubicación del banner, menciones en vivo, sorteos..."
              maxLength={500}
              className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors resize-none ${errors.content ? 'border-red-400' : 'border-background-200'}`}
            />
            <div className="flex justify-between mt-0.5">
              {errors.content && <p className="text-red-500 text-[9px]">{errors.content}</p>}
              <p className="text-foreground-300 text-[8px] ml-auto">{content.length}/500</p>
            </div>
          </div>

          {/* Preview revenue */}
          {price && startDate && endDate && new Date(endDate) > new Date(startDate) && (
            <div className="bg-emerald-50 rounded-xl p-3 flex items-center justify-between">
              <span className="text-emerald-700 text-[10px] font-medium">Revenue estimado (100% Radio)</span>
              <span className="text-emerald-700 text-sm font-bold">
                ${(Number(price) * Math.max(1, Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24 * 30)))).toLocaleString()}
              </span>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-background-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold cursor-pointer hover:bg-emerald-600 whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {submitting && <i className="ri-loader-4-line animate-spin text-sm"></i>}
            Crear Campaña
          </button>
        </div>
      </div>
    </div>
  );
}